import { BreadcrumbList, ListItem } from 'schema-dts';
import { helmetJsonLdProp } from 'react-schemaorg';
import { RoutingStore } from '../../stores/RoutingStore';
import { CategoriesStore } from '../../stores/CategoriesStore';
import { TFunction } from '../../../i18n/i18n';

interface GenerateActiveCategoryListItemParams {
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
}

function generateActiveCategoryListItemPropsV1({
  routingStore,
  categoriesStore,
}: GenerateActiveCategoryListItemParams): Pick<ListItem, 'item' | 'name'> | null {
  const { appPage } = routingStore;
  const activeCategory = appPage !== 'home' && categoriesStore.activeCategory;

  if (!activeCategory) {
    return null;
  }

  const { routes } = routingStore;

  return {
    item: routes.toAbsolute(routes.categoryRoute.get(activeCategory.id)),
    name: activeCategory.title,
  };
}

interface GenerateActiveSubCategoryListItemParams {
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
}

function generateActiveSubCategoryListItemPropsV1({
  routingStore,
  categoriesStore,
}: GenerateActiveSubCategoryListItemParams): Pick<ListItem, 'item' | 'name'> | null {
  const { activeSubCategory, activeCategory } = categoriesStore;

  if (!activeCategory || !activeSubCategory) {
    return null;
  }

  const { routes } = routingStore;

  return {
    item: routes.toAbsolute(routes.subCategoryRoute.get(activeCategory.id, activeSubCategory.id)),
    name: activeSubCategory.title,
  };
}

interface GenerateBreadcrumbListScriptPropsParamsV1 {
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
  t: TFunction;
}

type BreadcrumbListScriptProps = ReturnType<typeof helmetJsonLdProp>;

export function generateBreadcrumbListScriptPropsV1({
  t,
  routingStore,
  categoriesStore,
}: GenerateBreadcrumbListScriptPropsParamsV1): BreadcrumbListScriptProps {
  const { routes } = routingStore;

  return helmetJsonLdProp<BreadcrumbList>({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        name: t('breadcrumbs.wixcom'),
        item: 'https://www.wix.com/',
      },
      {
        name: t('breadcrumbs.templates'),
        item: routes.toAbsolute(routes.base.get()),
      },
      generateActiveCategoryListItemPropsV1({ routingStore, categoriesStore }),
      generateActiveSubCategoryListItemPropsV1({ routingStore, categoriesStore }),
    ]
      .filter((item) => item !== null)
      .map((params, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        ...params,
      })),
  });
}
